import { useCallback, useEffect, useRef, useState } from 'react';

import { getInternalTickets, getKpis } from '../../../hooks/jira';
import { InternalFilter, Kpis, TicketSummary } from '../../../types/responses/Jira';

export const FILTER_DEBOUNCE_MS = 400;

export const countBy = (items: TicketSummary[], key: 'requestType' | 'organizationName') => {
  const map = new Map<string, number>();
  items.forEach((i) => {
    const label = (i[key] as string) || '—';
    map.set(label, (map.get(label) || 0) + 1);
  });
  return Array.from(map.entries()).map(([label, count]) => ({ label, count })).sort((a, b) => b.count - a.count);
};

export const monthlyTrend = (items: TicketSummary[]) => {
  const map = new Map<string, number>();
  items.forEach((i) => {
    const month = (i.createdAt || '').slice(0, 7);
    if (month) map.set(month, (map.get(month) || 0) + 1);
  });
  return Array.from(map.entries()).map(([label, count]) => ({ label, count })).sort((a, b) => a.label.localeCompare(b.label));
};

// Remove entradas vazias (allowClear devolve '' / undefined) para nao enviar filtro vazio.
const clean = (f: InternalFilter): InternalFilter => {
  const out: Record<string, string> = {};
  Object.entries(f).forEach(([k, v]) => {
    if (typeof v === 'string' && v.trim() !== '') out[k] = v.trim();
  });
  return out as InternalFilter;
};

// Diferente de jira/tickets (useTickets.ts), o endpoint interno nao aceita `page=N`: ele pagina por
// um token opaco (JiraInternalService.list(filter, pageToken)) que so sabe andar pra frente — nao
// da pra "pular" pra uma pagina arbitraria so com o numero. Pra usar o mesmo molecule <Pagination>
// (que exige currentPage/totalCount) sem inventar uma capacidade que o backend nao tem, mantemos um
// cache local pagina -> token: toda vez que a pagina N responde com sucesso e nao e a ultima,
// guardamos o token dela como "o token pra pedir a pagina N+1" (tokenForPage). Isso permite voltar
// pra uma pagina ja visitada (o token dela ja esta no cache) sem reconsultar o servidor em ordem; so
// a fronteira (maxKnownPage + 1) de fato avanca a consulta. Pedir uma pagina cujo token nao foi
// descoberto ainda (p.ex. apos um filtro novo zerar o cache) e um no-op defensivo — nunca deveria
// acontecer porque a UI (Pagination) so oferece paginas ja conhecidas ou a propria fronteira.
// Igual useTickets.ts, totalCount e so maxKnownPage (+1 se a fronteira ainda tiver proxima pagina),
// nunca um total real que o backend nunca devolve; `hasNext` continua exposto como atalho derivado.
interface Pagination {
  page: number;
  maxKnownPage: number;
  frontierHasNext: boolean;
}

const INITIAL_PAGINATION: Pagination = { page: 1, maxKnownPage: 1, frontierHasNext: false };

export const useInternalTickets = () => {
  const [items, setItems] = useState<TicketSummary[]>([]);
  const [kpis, setKpis] = useState<Kpis | null>(null);
  const [draft, setDraft] = useState<InternalFilter>({});
  const [applied, setApplied] = useState<InternalFilter>({});
  const [pagination, setPagination] = useState<Pagination>(INITIAL_PAGINATION);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [forbidden, setForbidden] = useState(false);
  const mounted = useRef(true);
  const listSeq = useRef(0);
  const kpiSeq = useRef(0);
  // pagina -> token necessario pra pedir aquela pagina; a pagina 1 nunca precisa de token.
  const tokenForPage = useRef<Map<number, string | undefined>>(new Map([[1, undefined]]));

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  // O filtro digitado atualiza na hora (campos controlados); a consulta so sai apos 400 ms parado.
  useEffect(() => {
    const id = setTimeout(() => {
      const next = clean(draft);
      // mesmo conteudo => mesma referencia: nao dispara consulta repetida (ex.: na montagem)
      setApplied((prev) => (JSON.stringify(prev) === JSON.stringify(next) ? prev : next));
    }, FILTER_DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [draft]);

  const handle = useCallback((e: any) => {
    if (e?.response?.status === 403) setForbidden(true);
    else setError(true);
  }, []);

  const load = useCallback(
    // `preserveFrontier`: só true quando p===1 vem de um clique explícito no botão "1" do
    // <Pagination> (ver setPage) — paginação estável, mesmo filtro, tokens de páginas 2+ continuam
    // válidos. Troca de filtro/refresh continuam invalidando tudo (dataset pode ter mudado de verdade).
    async (p: number, preserveFrontier = false): Promise<boolean> => {
      // Recarregar a pagina 1 (filtro novo ou refresh) invalida qualquer token conhecido na hora:
      // nenhuma pagina alem da 1 vale enquanto essa resposta nao chegar, com sucesso ou nao — o
      // mesmo efeito do reset preemptivo de nextRef que a versao anterior fazia antes do fetch.
      if (p === 1 && !preserveFrontier) {
        tokenForPage.current = new Map([[1, undefined]]);
        setPagination(INITIAL_PAGINATION);
      } else if (!tokenForPage.current.has(p)) {
        return false;
      }
      const token = p === 1 ? undefined : tokenForPage.current.get(p);
      listSeq.current += 1;
      const seq = listSeq.current;
      setLoading(true);
      try {
        const r = await getInternalTickets(token ? { ...applied, pageToken: token } : applied);
        if (!mounted.current || seq !== listSeq.current) return false;
        setItems(r.items);
        if (!r.isLastPage) tokenForPage.current.set(p + 1, r.nextPageToken ?? undefined);
        setPagination((prev) => {
          if (p === 1 && preserveFrontier) return { ...prev, page: p };
          const isFrontier = p === 1 || p >= prev.maxKnownPage;
          return {
            page: p,
            maxKnownPage: p === 1 ? 1 : Math.max(prev.maxKnownPage, p),
            frontierHasNext: isFrontier ? !r.isLastPage : prev.frontierHasNext,
          };
        });
        setError(false);
        return true;
      } catch (e) {
        if (mounted.current && seq === listSeq.current) handle(e);
        return false;
      } finally {
        if (mounted.current && seq === listSeq.current) setLoading(false);
      }
    },
    [applied, handle],
  );

  // Sempre a versao mais recente de load: o efeito de filtro e o KPI nao usam filtros velhos.
  const loadRef = useRef(load);
  loadRef.current = load;

  const loadKpis = useCallback(async () => {
    kpiSeq.current += 1;
    const seq = kpiSeq.current;
    try {
      const k = await getKpis();
      if (!mounted.current || seq !== kpiSeq.current) return;
      setKpis(k);
    } catch (e) {
      if (mounted.current && seq === kpiSeq.current) handle(e);
    }
  }, [handle]);

  // carga inicial e toda vez que o filtro aplicado muda: sempre volta pra pagina 1
  useEffect(() => {
    loadRef.current(1);
  }, [applied]);

  useEffect(() => {
    loadKpis();
  }, [loadKpis]);

  const refresh = useCallback(async () => {
    setError(false);
    await Promise.all([loadRef.current(1), loadKpis()]);
  }, [loadKpis]);

  const totalCount = pagination.maxKnownPage + (pagination.frontierHasNext ? 1 : 0);

  return {
    items,
    kpis,
    filter: draft,
    setFilter: setDraft,
    loading,
    error,
    forbidden,
    page: pagination.page,
    pageSize: 1,
    totalCount,
    hasNext: pagination.page < totalCount,
    refresh,
    setPage: (p: number) => loadRef.current(p, true),
    byType: countBy(items, 'requestType'),
    byClient: countBy(items, 'organizationName'),
    trend: monthlyTrend(items),
  };
};
